import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchOutreachQueue,
  fetchBdHighIntent,
  appendOutreachLog,
  excludeContact,
  updateContact,
  saveNextAction,
  updateTrial,
  aiFilterOutreach,
  fetchOutreachAnalytics,
  fetchAnalyticsDrill,
  fetchSentimentInbox,
  fetchOutreachThread,
  patchReplySentiment,
  backfillGmailBodies,
  reclassifyAllSentiment,
  dedupOutreachLog,
  fetchConfig,
  updateConfig,
  fetchContactsPage,
  setContactLane,
  ignoreContact,
} from '../api/backend.js';

const BASE = import.meta.env.VITE_BACKEND_BASE_URL;

function RecentMeetingsPanel({ getToken }) {
  const [notes, setNotes] = useState(null);
  const [open, setOpen] = useState(null);
  const [filter, setFilter] = useState('');
  const [limit, setLimit] = useState(20);

  useEffect(() => {
    if (!getToken) return;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${BASE}/api/zoom-meeting-notes?limit=100`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setNotes(data.notes ?? []);
      } catch { setNotes([]); }
    })();
  }, [getToken]);

  function parseEx(raw) {
    try {
      const p = JSON.parse(raw ?? '{}');
      return Array.isArray(p) ? { items: p.map(s => ({ assignee: '', task: s })), attendees: [] }
        : { items: p.items ?? [], attendees: p.attendees ?? [] };
    } catch { return { items: [], attendees: [] }; }
  }

  function relDate(ts) {
    const d = new Date(ts?.value ?? ts ?? 0);
    if (isNaN(d)) return '';
    const diff = Math.round((Date.now() - d) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    if (diff < 7) return `${diff}d ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  if (notes === null) return <div style={{ padding: '16px 20px', fontSize: 13, color: 'var(--text-3)' }}>Loading meetings…</div>;

  const q = filter.toLowerCase();
  const filtered = q
    ? notes.filter(n => (n.meeting_name || n.subject || '').toLowerCase().includes(q))
    : notes;
  const visible = filtered.slice(0, limit);

  return (
    <div style={{ padding: '16px 20px' }}>
      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: 'var(--navy)' }}>🎥 Meeting Notes</div>
      {notes.length > 0 && (
        <input
          value={filter}
          onChange={e => { setFilter(e.target.value); setLimit(20); }}
          placeholder="Filter by meeting name…"
          style={{
            width: '100%', boxSizing: 'border-box', marginBottom: 10,
            padding: '6px 10px', fontSize: 12, borderRadius: 6,
            border: '1px solid var(--border)', background: 'var(--surface)',
            color: 'var(--text-1)', outline: 'none',
          }}
        />
      )}
      {filtered.length === 0 && (
        <div style={{ fontSize: 13, color: 'var(--text-3)' }}>
          {notes.length === 0 ? 'No meeting notes yet. Gmail sync will pick up Zoom Meeting Assets emails automatically.' : 'No matches.'}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {visible.map(n => {
          const isOpen = open === n.id;
          const { items, attendees } = parseEx(n.action_items);
          return (
            <div key={n.id} style={{ border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface-2)', fontSize: 12 }}>
              <button onClick={() => setOpen(isOpen ? null : n.id)}
                style={{ width: '100%', textAlign: 'left', padding: '9px 12px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', gap: 10, alignItems: 'baseline' }}>
                <span style={{ fontWeight: 600, flex: 1, fontSize: 13 }}>{n.meeting_name || n.subject}</span>
                <span style={{ color: 'var(--text-3)', fontSize: 11, whiteSpace: 'nowrap' }}>
                  {relDate(n.received_at)}
                </span>
                <span style={{ color: 'var(--text-3)', fontSize: 10 }}>{isOpen ? '▲' : '▼'}</span>
              </button>
              {isOpen && (
                <div style={{ padding: '0 12px 12px', borderTop: '1px solid var(--border)' }}>
                  {attendees.length > 0 && (
                    <div style={{ marginTop: 8, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {attendees.map((a, i) => (
                        <span key={i} style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10, background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-3)' }}>{a}</span>
                      ))}
                    </div>
                  )}
                  {n.summary && <p style={{ marginTop: 8, lineHeight: 1.6, color: 'var(--text-1)' }}>{n.summary}</p>}
                  {items.length > 0 && (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: 6 }}>Next Steps</div>
                      {items.map((it, i) => (
                        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 5, alignItems: 'flex-start' }}>
                          {it.assignee && <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 3, background: '#eff6ff', color: '#2563eb', fontWeight: 700, flexShrink: 0 }}>{it.assignee}</span>}
                          <span style={{ color: 'var(--text-1)' }}>{it.task || it}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {!n.summary && items.length === 0 && (
                    <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-3)' }}>Extraction pending — will populate on next Gmail sync.</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {filtered.length > limit && (
        <button
          onClick={() => setLimit(l => l + 20)}
          style={{ marginTop: 12, width: '100%', padding: '7px', fontSize: 12, borderRadius: 6, border: '1px solid var(--border)', background: 'none', cursor: 'pointer', color: 'var(--text-3)' }}
        >
          Show more ({filtered.length - limit} remaining)
        </button>
      )}
    </div>
  );
}
import CadencePanel from '../components/CadencePanel.jsx';

// ── Priority scoring ──────────────────────────────────────────────────────────

// A bdEntry comes from /api/bd-high-intent, which returns engagement counts
// (read_count / url_link_views / file_link_views), not an event_type.
function bdHasClick(bdEntry) {
  return Number(bdEntry?.url_link_views || 0) > 0 || Number(bdEntry?.file_link_views || 0) > 0;
}

function computePriority(r, ampEntry, bdEntry) {
  let score = 0;
  const now = new Date();
  // Manually pinned by a rep — they know something the system doesn't. Float it up.
  if (r.is_pinned) score += 40;
  // Backend priority_tier drives urgency ranking.
  // -1 = Zoom follow-up due today/overdue (committed action)
  //  0 = unanswered inbound question
  //  1 = hot/warm deal trending up
  //  2 = active trial
  //  3 = post-meeting silence (meeting happened, no follow-up email yet)
  //  4 = hot/warm deal
  if (r.priority_tier === -1) score += 65;
  else if (r.priority_tier === 0) score += 45;
  else if (r.priority_tier === 1) score += 25;
  else if (r.priority_tier === 2) score += 12;
  else if (r.priority_tier === 3) score += 20;
  if (r.trial_end_date) {
    const daysLeft = Math.ceil((new Date(r.trial_end_date) - now) / 86400000);
    // Only active (future) trials get the urgency boost. Expired trials get
    // nothing here so they sink instead of topping the list (spec: deprioritize).
    if (daysLeft < 0) { /* expired — no boost */ }
    else if (daysLeft <= 3) score += 60;
    else if (daysLeft <= 7) score += 50;
    else if (daysLeft <= 14) score += 35;
    else if (daysLeft <= 30) score += 20;
  }
  // Strong ICP fit — worth ranking up within its lane, whether or not
  // they've been contacted before.
  if (r.icp_fit != null) {
    score += Math.round(Number(r.icp_fit) * 0.3); // up to +30
  }
  if (r.is_trial) score += 10;
  if (r.reply_sentiment === 'positive') score += 25;
  else if (r.reply_sentiment === 'neutral') score += 10;
  if ((r.next_action || '').trim()) score += 10;
  if ((r.deal_tier || '').toLowerCase() === 'hot') score += 12;
  else if ((r.deal_tier || '').toLowerCase() === 'warm') score += 8;
  if ((r.deal_trend || '').toLowerCase() === 'up') score += 6;
  if ((r.deal_confidence || '').toLowerCase() === 'low') score -= 5;
  if (bdEntry) {
    if (bdHasClick(bdEntry)) score += 15;
    else score += 10;
  }
  if (ampEntry) {
    const tier = ampEntry.tier || '';
    if (tier === 'healthy') score += 12;
    else if (tier === 'watch') score += 8;
    else if (tier === 'at_risk') score += 5;
  }
  if (r.days_since_last_outreach != null) {
    const d = Number(r.days_since_last_outreach);
    if (d >= 21) score += 15;
    else if (d >= 14) score += 10;
    else if (d >= 7) score += 5;
  }
  const stage = (r.stage || '').toLowerCase();
  if (stage === 'demo_done' || stage === 'trial') score += 8;
  else if (stage === 'interested') score += 5;
  return Math.min(score, 100);
}

function getSignals(r, ampEntry, bdEntry) {
  const chips = [];
  if (r.is_pinned) chips.push({ label: 'Manually added', tone: 'purple' });
  // Zoom-sourced committed follow-up
  if (r.priority_tier === -1 && r.next_action_due) {
    const daysOverdue = Math.floor((Date.now() - new Date(r.next_action_due).getTime()) / 86400000);
    const label = daysOverdue > 0 ? `Follow-up overdue ${daysOverdue}d (Zoom)` : 'Follow-up due today (Zoom)';
    chips.push({ label, tone: 'red' });
  }
  if (r.priority_tier === 0) chips.push({ label: 'Unanswered question', tone: 'red' });
  // Post-meeting silence
  if (r.days_post_meeting_silence != null && r.days_post_meeting_silence >= 1) {
    chips.push({ label: `${r.days_post_meeting_silence}d since meeting, no follow-up`, tone: 'amber' });
  }
  if (r.profile_completeness != null && r.profile_completeness < 70) {
    chips.push({ label: `${r.profile_completeness}% profile`, tone: 'amber' });
  }
  if (bdEntry) {
    const label = Number(bdEntry.file_link_views || 0) > 0 ? 'File viewed'
      : Number(bdEntry.url_link_views || 0) > 0 ? 'Link clicked'
      : Number(bdEntry.read_count || 0) >= 2 ? `Opened ${bdEntry.read_count}×`
      : 'Email opened';
    chips.push({ label, tone: 'green' });
  }
  if (r.reply_sentiment === 'positive') chips.push({ label: 'Replied (+)', tone: 'green' });
  else if (r.reply_sentiment === 'negative') chips.push({ label: 'Replied (−)', tone: 'red' });
  else if (r.reply_sentiment === 'neutral') chips.push({ label: 'Replied', tone: 'blue' });
  if ((r.next_action || '').trim()) chips.push({ label: `Next: ${r.next_action}`, tone: 'purple' });
  if (r.trial_end_date) {
    const daysLeft = Math.ceil((new Date(r.trial_end_date) - new Date()) / 86400000);
    if (daysLeft <= 7) chips.push({ label: `Trial ends in ${daysLeft}d`, tone: 'amber' });
    else chips.push({ label: `Trial ends ${r.trial_end_date}`, tone: 'purple' });
  }
  if (ampEntry) {
    const tier = ampEntry.tier || '';
    if (tier === 'at_risk' || tier === 'churned') chips.push({ label: `Amplitude: ${tier}`, tone: 'red' });
    else if (tier === 'healthy') chips.push({ label: 'Active user', tone: 'green' });
  }
  if (r.days_since_last_outreach != null && Number(r.days_since_last_outreach) >= 14) {
    chips.push({ label: `${r.days_since_last_outreach}d silent`, tone: 'gray' });
  }
  return chips;
}

function sentimentAction(sentiment) {
  const s = String(sentiment || '').toLowerCase();
  if (s === 'positive') return { label: 'Follow up now', tone: 'green' };
  if (s === 'question') return { label: 'Answer first', tone: 'amber' };
  if (s === 'not_now') return { label: 'Remind later', tone: 'purple' };
  if (s === 'negative') return { label: 'Deprioritize', tone: 'red' };
  return { label: 'Low priority', tone: 'gray' };
}

function getThreadState(row) {
  const sent = String(row.reply_sentiment || '').toLowerCase();
  const latest = String(row.latest_reply_sentiment || '').toLowerCase();
  const answered = !!row.answered;
  const positiveish = ['positive', 'question'].includes(sent) || ['positive', 'question'].includes(latest);
  const negativeish = sent === 'negative' || latest === 'negative';
  if (negativeish && !answered) return { label: 'closed', tone: 'gray', detail: 'Explicit decline' };
  if (negativeish && answered) return { label: 'pending_followup', tone: 'amber', detail: 'Decline / objection handled' };
  if (sent === 'question' && !answered) return { label: 'open_question', tone: 'amber', detail: 'Waiting on answer' };
  if (sent === 'question' && answered) return { label: 'pending_followup', tone: 'amber', detail: 'Answered, still waiting' };
  if (sent === 'positive' && !answered) return { label: 'open_positive', tone: 'green', detail: 'Interested, no reply yet' };
  if (sent === 'positive' && answered) return { label: 'pending_followup', tone: 'amber', detail: 'Interest handled, waiting' };
  if (answered && positiveish) return { label: 'pending_followup', tone: 'amber', detail: 'Needs next step' };
  if (answered) return { label: 'resolved', tone: 'blue', detail: 'Answered' };
  return { label: 'open', tone: 'gray', detail: 'No response yet' };
}

// Days until a contact's trial ends (negative = already expired), or null.
function trialDaysLeft(r) {
  if (!r.trial_end_date) return null;
  return Math.ceil((new Date(r.trial_end_date) - new Date()) / 86400000);
}

// ── Outreach lanes ──────────────────────────────────────────────────────────
// Each contact is assigned exactly one lane, checked top-down. Every lane has a
// distinct action point so the rep knows what to actually do.
const LANE_ORDER = ['pinned', 'hot', 'trial', 'followup', 'icp', 'dormant'];
const LANE_META = {
  pinned:   { label: 'Manually added',    action: 'You flagged these — your call',        accent: 'var(--purple)', tone: 'purple' },
  hot:      { label: 'Hot · high intent',  action: 'Reply or book a call now',             accent: 'var(--red)',    tone: 'red' },
  trial:    { label: 'Trial in flight',    action: 'Drive activation before it ends',      accent: 'var(--amber)',  tone: 'amber' },
  followup: { label: 'Awaiting follow-up', action: 'Send your next follow-up',             accent: 'var(--blue, #2563eb)', tone: 'blue' },
  icp:      { label: 'Top ICP to open',    action: 'Strong fit, worth a first or next email', accent: 'var(--green)',  tone: 'green' },
  dormant:  { label: 'Dormant · low priority', action: 'Skip unless you know something',   accent: 'var(--text-3)', tone: 'gray' },
};

// Infer a lane from free-text notes / next_action / AI summary. Null if nothing matches.
function notesLane(r) {
  const text = `${r.notes || ''} ${r.next_action || ''} ${r.ai_summary || ''}`.toLowerCase();
  if (!text.trim()) return null;
  if (/\bignore\b|not interested|no interest|\bpass\b|\bdead\b/.test(text)) return 'dormant';
  if (/follow.?up|followup|nudge|check ?in|circle back|touch base/.test(text)) return 'followup';
  if (/\btrial\b/.test(text)) return 'trial';
  if (/\bcall\b|\bdemo\b|interested|schedule|meeting|book a/.test(text)) return 'hot';
  return null;
}

function assignLane(r, ampEntry, bdEntry) {
  // 1) Manual override always wins. 2) Notes keywords. 3) Pinned. 4) Auto signals.
  if (r.outreach_lane && LANE_META[r.outreach_lane]) return r.outreach_lane;
  const nl = notesLane(r);
  if (nl) return nl;
  if (r.is_pinned) return 'pinned';
  const sent = String(r.reply_sentiment || '').toLowerCase();
  const latestSent = String(r.latest_reply_sentiment || '').toLowerCase();
  const highIntent = ['positive', 'question'].includes(sent) || ['positive', 'question'].includes(latestSent);
  // High intent: they engaged.
  if (highIntent) {
    // If they engaged but we already sent a reply and they went quiet, keep them
    // in follow-up instead of assuming it's closed.
    if (r.days_since_last_outreach != null && Number(r.outbound_count || 0) > 0) return 'followup';
    return 'hot';
  }
  if (bdHasClick(bdEntry)) return 'hot';
  if (ampEntry && ampEntry.tier === 'healthy') return 'hot';
  if ((r.deal_tier || '').toLowerCase() === 'hot') return 'hot';
  const dl = trialDaysLeft(r);
  if (r.is_trial && dl != null && dl < 0) return 'dormant'; // expired trial — deprioritize
  if (dl != null && dl >= 0) return 'trial';                // active trial
  if (sent === 'negative') return 'dormant';
  if (latestSent === 'negative') return 'dormant';
  // Emailed, no reply, due for a nudge, not yet exhausted (5d / max 4 touches).
  if (r.days_since_last_outreach != null && r.days_since_last_outreach >= 5 && Number(r.outbound_count || 0) <= 4) return 'followup';
  // Strong ICP fit that hasn't earned a more specific lane above — whether
  // never contacted (cold-email candidate) or contacted with no reply/negative
  // signal yet (worth reopening). Anyone who actually replied positive or is
  // otherwise engaged already got claimed by `highIntent`/trial/followup above,
  // so this never competes with those — it only catches good-fit contacts with
  // no live signal either way.
  if (Number(r.icp_fit || 0) >= 55) return 'icp';
  return 'dormant';
}

// Maps to CSS vars from the design system
function priorityAccent(score) {
  if (score >= 60) return 'var(--red)';
  if (score >= 30) return 'var(--amber)';
  return 'var(--border-2)';
}

function priorityLabel(score) {
  if (score >= 60) return 'Urgent';
  if (score >= 30) return 'Recommended';
  return 'Queue';
}

// ── Snooze helpers ────────────────────────────────────────────────────────────

const SNOOZE_KEY = 'snoozedOutreachContacts';

function getSnoozed() {
  try { return JSON.parse(localStorage.getItem(SNOOZE_KEY) || '{}'); } catch { return {}; }
}

function snoozeContact(id, until) {
  const map = getSnoozed();
  if (until) map[id] = until;
  else delete map[id];
  localStorage.setItem(SNOOZE_KEY, JSON.stringify(map));
}

function isSnoozed(id) {
  const map = getSnoozed();
  const d = map[id];
  if (!d) return false;
  if (d <= new Date().toISOString().slice(0, 10)) {
    snoozeContact(id, null);
    return false;
  }
  return true;
}

// ── Signal chip ───────────────────────────────────────────────────────────────

function SignalChip({ label, tone }) {
  const map = {
    green:  { bg: 'var(--green-bg)',  color: 'var(--green)' },
    red:    { bg: 'var(--red-bg)',    color: 'var(--red)' },
    amber:  { bg: 'var(--amber-bg)', color: 'var(--amber)' },
    blue:   { bg: 'var(--blue-bg)',  color: 'var(--blue)' },
    purple: { bg: 'var(--purple-bg)',color: 'var(--purple)' },
    gray:   { bg: 'var(--surface-2)',color: 'var(--text-3)' },
  };
  const s = map[tone] || map.gray;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      background: s.bg, color: s.color,
      borderRadius: 999, padding: '2px 8px',
      fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
      lineHeight: 1.5,
    }}>
      {label}
    </span>
  );
}

// ── Section label ─────────────────────────────────────────────────────────────

function SectionLabel({ children }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
      textTransform: 'uppercase', color: 'var(--text-3)',
      marginBottom: 8,
    }}>
      {children}
    </div>
  );
}

// ── Info tile ─────────────────────────────────────────────────────────────────

function InfoTile({ label, value }) {
  return (
    <div style={{
      background: 'var(--surface-2)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-sm)', padding: '7px 10px',
    }}>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{value}</div>
    </div>
  );
}

// ── Group header (left list) ──────────────────────────────────────────────────

function GroupHeader({ label, count, accent, sub, collapsed, onToggle, right }) {
  return (
    <button
      onClick={onToggle}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        width: '100%', padding: '6px 14px',
        background: 'var(--surface-2)',
        border: 'none',
        borderBottom: '1px solid var(--border)',
        borderLeft: `3px solid ${accent}`,
        cursor: 'pointer', textAlign: 'left',
      }}
    >
      <span style={{ fontSize: 8, color: 'var(--text-3)', flexShrink: 0 }}>{collapsed ? '▶' : '▼'}</span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0, flex: 1 }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: accent }}>
          {label} · {count}
        </span>
        {sub && <span style={{ fontSize: 10.5, fontWeight: 500, color: 'var(--text-3)', textTransform: 'none', letterSpacing: 0 }}>{sub}</span>}
      </span>
      {right}
    </button>
  );
}

// ── Contact row (left pane) ───────────────────────────────────────────────────

const LATEST_SENT_CHIP = {
  positive:     { bg: '#dcfce7', text: '#15803d', label: '↑ Positive' },
  negative:     { bg: '#fee2e2', text: '#dc2626', label: '↓ Concern' },
  question:     { bg: '#fef3c7', text: '#92400e', label: '? Question' },
  unsubscribed: { bg: '#fce7f3', text: '#be185d', label: 'Unsubscribed' },
  unsubscribe:  { bg: '#fce7f3', text: '#be185d', label: 'Unsubscribed' },
};


function ContactRow({ r, ampEntry, bdEntry, selected, onClick }) {
  const score = r._priority ?? 0;
  const accent = priorityAccent(score);
  const signals = getSignals(r, ampEntry, bdEntry);
  const topSignal = signals[0];
  const sentChip = r.latest_reply_sentiment ? LATEST_SENT_CHIP[r.latest_reply_sentiment.toLowerCase()] : null;
  const sentDaysAgo = r.latest_reply_sentiment_ts
    ? Math.floor((Date.now() - new Date(r.latest_reply_sentiment_ts).getTime()) / 86400000)
    : null;
  const sentStale = sentDaysAgo !== null && sentDaysAgo > 90;
  const lane = assignLane(r, ampEntry, bdEntry);
  const laneMeta = LANE_META[lane];

  return (
    <button
      id={`orow-${r.contact_id}`}
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        width: '100%', padding: '10px 14px',
        borderLeft: `3px solid ${accent}`,
        background: selected ? 'var(--orange-light)' : 'transparent',
        border: 'none',
        borderBottom: '1px solid var(--border)',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'background 0.1s',
      }}
      onMouseEnter={e => { if (!selected) e.currentTarget.style.background = 'var(--surface-2)'; }}
      onMouseLeave={e => { if (!selected) e.currentTarget.style.background = 'transparent'; }}
    >
      {/* Score circle */}
      <div style={{
        width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
        background: accent, color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 10, fontWeight: 700,
      }}>
        {score}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 120 }}>
            {r.name || r.email}
          </span>
          {sentChip && (
            <span title={sentStale ? `${sentDaysAgo}d ago — may be stale` : `${sentDaysAgo}d ago`}
              style={{ fontSize: 9.5, fontWeight: 600, padding: '1px 6px', borderRadius: 8, flexShrink: 0,
                background: sentStale ? 'var(--surface-2)' : sentChip.bg,
                color: sentStale ? 'var(--text-3)' : sentChip.text,
                fontStyle: sentStale ? 'italic' : 'normal',
                border: sentStale ? '1px solid var(--border)' : 'none',
              }}>
              {sentChip.label}{sentStale ? ' ·' : ''}
            </span>
          )}
          <span style={{
            fontSize: 9.5, fontWeight: 700, padding: '1px 6px', borderRadius: 8, flexShrink: 0,
            background: laneMeta.tone === 'green' ? 'var(--green-bg)'
              : laneMeta.tone === 'amber' ? 'var(--amber-bg)'
              : laneMeta.tone === 'purple' ? 'var(--purple-bg)'
              : laneMeta.tone === 'blue' ? 'rgba(37,99,235,0.1)'
              : laneMeta.tone === 'red' ? 'var(--red-bg)' : 'var(--surface-2)',
            color: laneMeta.tone === 'green' ? 'var(--green)'
              : laneMeta.tone === 'amber' ? 'var(--amber)'
              : laneMeta.tone === 'purple' ? 'var(--purple)'
              : laneMeta.tone === 'blue' ? 'var(--blue, #2563eb)'
              : laneMeta.tone === 'red' ? 'var(--red)' : 'var(--text-3)',
          }} title={laneMeta.action}>
            {laneMeta.label}
          </span>
          {topSignal && <SignalChip label={topSignal.label} tone={topSignal.tone} />}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {[r.firm_name, r.role].filter(Boolean).join(' · ')}
        </div>
        {/* Notes context: summary or tags */}
        {(r.next_action || r.ai_summary) && (
          <div style={{
            fontSize: 10.5, fontStyle: 'italic', marginTop: 3,
            padding: '2px 6px', borderRadius: 4,
            background: 'rgba(234,179,8,0.08)', borderLeft: '2px solid rgba(234,179,8,0.5)',
            color: 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {r.next_action || r.ai_summary}
          </div>
        )}
        {!r.ai_summary && r.ai_tags && (
          <div style={{ display: 'flex', gap: 3, flexWrap: 'nowrap', overflow: 'hidden', marginTop: 3 }}>
            {r.ai_tags.split(',').slice(0, 3).map((t) => t.trim()).filter(Boolean).map((tag) => (
              <span key={tag} style={{ fontSize: 9.5, padding: '1px 5px', borderRadius: 99, background: 'rgba(234,179,8,0.08)', color: 'var(--amber,#b45309)', border: '1px solid rgba(234,179,8,0.25)', flexShrink: 0 }}>
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {r.days_since_last_outreach != null && (
        <div style={{ fontSize: 10, color: 'var(--text-3)', flexShrink: 0 }}>
          {r.days_since_last_outreach}d
        </div>
      )}
    </button>
  );
}

// ── Detail panel (right pane) ─────────────────────────────────────────────────

function DetailPanel({ r, ampEntry, bdEntry, trials, getToken, onLogged, onExcluded, onSnooze, onIgnored, onOpenTimeline, onContactUpdate }) {
  const [emailState, setEmailState] = useState('idle'); // idle | sent | emailed
  const [logNote, setLogNote] = useState('');
  const [nextAction, setNextAction] = useState(r.next_action || '');
  const [nextActionDue, setNextActionDue] = useState(r.next_action_due || '');
  const [actionSaving, setActionSaving] = useState(false);
  const [showSnooze, setShowSnooze] = useState(false);
  const [snoozeDate, setSnoozeDate] = useState('');
  const [showExclude, setShowExclude] = useState(false);
  const [excludeReason, setExcludeReason] = useState('');
  const [busy, setBusy] = useState(false);

  const snoozedUntil = getSnoozed()[r.contact_id];
  const signals = getSignals(r, ampEntry, bdEntry);
  const score = r._priority ?? 0;
  const accent = priorityAccent(score);
  const actionMeta = sentimentAction(r.reply_sentiment);

  useEffect(() => {
    setNextAction(r.next_action || '');
    setNextActionDue(r.next_action_due || '');
  }, [r.contact_id, r.next_action, r.next_action_due]);

  async function handleLog() {
    setBusy(true);
    try {
      const notePrefix = nextAction.trim() ? `[Next action: ${nextAction}${nextActionDue ? ` | due ${nextActionDue}` : ''}] ` : '';
      await appendOutreachLog(getToken, {
        contact_id: r.contact_id,
        email: r.email,
        outreach_type: 'email',
        note: notePrefix + (logNote || 'Email sent'),
        stage: r.stage,
      });
      const activeTrial = trials?.find((t) => t.contact_id === r.contact_id && String(t.status || '').toLowerCase() === 'active');
      if (activeTrial?.trial_id) {
        const trialNote = `${new Date().toLocaleDateString()} — ${logNote || nextAction || 'Contacted via Outreach'}`;
        await updateTrial(getToken, activeTrial.trial_id, {
          notes: activeTrial.notes ? `${activeTrial.notes}\n${trialNote}` : trialNote,
        });
      }
      setEmailState('idle');
      setLogNote('');
      onLogged?.(r.contact_id);
    } catch (e) {
      alert('Log failed: ' + e.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleSaveNextAction() {
    setActionSaving(true);
    try {
      await saveNextAction(getToken, r.contact_id, nextAction.trim(), nextActionDue || '');
      onContactUpdate?.(r.contact_id, { next_action: nextAction.trim(), next_action_due: nextActionDue || '' });
    } catch (e) {
      alert('Could not save next action: ' + e.message);
    } finally {
      setActionSaving(false);
    }
  }

  async function handleExclude() {
    if (!excludeReason.trim()) return;
    setBusy(true);
    try {
      // Save exclusion flag + reason; also append reason to contact notes
      const existingNotes = r.notes ? r.notes.trimEnd() + '\n\n' : '';
      const noteEntry = `[Not my target — ${new Date().toLocaleDateString()}] ${excludeReason}`;
      await Promise.all([
        excludeContact(getToken, r.contact_id, excludeReason),
        updateContact(getToken, r.contact_id, { notes: existingNotes + noteEntry }),
      ]);
      onExcluded?.(r.contact_id);
    } catch (e) {
      alert('Failed: ' + e.message);
    } finally {
      setBusy(false);
    }
  }

  function handleSnooze() {
    if (!snoozeDate) return;
    snoozeContact(r.contact_id, snoozeDate);
    setShowSnooze(false);
    setSnoozeDate('');
    onSnooze?.(r.contact_id);
  }

  async function handleSetLane(lane) {
    try {
      await setContactLane(getToken, r.contact_id, lane);
      onContactUpdate?.(r.contact_id, { outreach_lane: lane });
    } catch (e) {
      alert('Could not set category: ' + e.message);
    }
  }

  // "Already emailed" — requires a log note, records the outbound touch, then
  // moves the contact into the Awaiting follow-up lane.
  async function handleAlreadyEmailed() {
    if (!logNote.trim()) return;
    setBusy(true);
    try {
      await appendOutreachLog(getToken, {
        contact_id: r.contact_id, email: r.email, outreach_type: 'email',
        note: logNote, stage: r.stage,
      });
      await setContactLane(getToken, r.contact_id, 'followup');
      onContactUpdate?.(r.contact_id, { outreach_lane: 'followup' });
      onLogged?.(r.contact_id);
    } catch (e) {
      alert('Log failed: ' + e.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleIgnore() {
    setBusy(true);
    try {
      await ignoreContact(getToken, r.contact_id, true);
      onIgnored?.(r.contact_id);
    } catch (e) {
      alert('Ignore failed: ' + e.message);
    } finally {
      setBusy(false);
    }
  }

  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 1);
  const subject = r.last_outreach_subject ? `Re: ${r.last_outreach_subject}` : 'Following up';
  const mailto = `mailto:${r.email}?subject=${encodeURIComponent(subject)}`;
  const firstName = (r.name || '').split(' ')[0] || '';

  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Header */}
      <div style={{ borderBottom: '1px solid var(--border)', paddingBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--navy)', lineHeight: 1.3 }}>
              {r.name || r.email}
            </h2>
            <div style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 3 }}>
              {[r.role, r.firm_name].filter(Boolean).join(' · ')}
            </div>
            {r.sector_coverage && (
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 1 }}>{r.sector_coverage}</div>
            )}
          </div>
          <div style={{
            flexShrink: 0, padding: '3px 10px', borderRadius: 999,
            background: score >= 60 ? 'var(--red-bg)' : score >= 30 ? 'var(--amber-bg)' : 'var(--surface-2)',
            color: accent,
            fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
            border: `1px solid ${accent}22`,
          }}>
            {priorityLabel(score)} · {score}
          </div>
        </div>
        {r.email && (
          <a href={`mailto:${r.email}`} style={{ fontSize: 12, color: 'var(--orange)', fontWeight: 500 }}>
            {r.email}
          </a>
        )}
      </div>

      {/* Signals */}
        {signals.length > 0 && (
          <div>
            <SectionLabel>Why now</SectionLabel>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {signals.map((s, i) => <SignalChip key={i} label={s.label} tone={s.tone} />)}
            </div>
          </div>
        )}

      {/* Category — manual lane override */}
      <div>
        <SectionLabel>Category</SectionLabel>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <select
            value={r.outreach_lane && LANE_META[r.outreach_lane] ? r.outreach_lane : ''}
            onChange={(e) => handleSetLane(e.target.value)}
            style={{
              padding: '6px 10px', borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)', fontSize: 12,
              background: 'var(--surface)', color: 'var(--text)', fontWeight: 600,
            }}
          >
            <option value="">Auto ({LANE_META[assignLane(r, ampEntry, bdEntry)]?.label ?? '—'})</option>
            {LANE_ORDER.map((id) => (
              <option key={id} value={id}>{LANE_META[id].label}</option>
            ))}
          </select>
          <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
            {r.outreach_lane && LANE_META[r.outreach_lane]
              ? 'Manually set — overrides auto & notes'
              : 'Auto — from signals & notes'}
          </span>
        </div>
      </div>

      <div>
        <SectionLabel>Recommended action</SectionLabel>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{
            fontSize: 12, fontWeight: 700, padding: '3px 8px', borderRadius: 999,
            background: actionMeta.tone === 'green' ? 'var(--green-bg)'
              : actionMeta.tone === 'amber' ? 'var(--amber-bg)'
              : actionMeta.tone === 'purple' ? 'var(--purple-bg)'
              : actionMeta.tone === 'red' ? 'var(--red-bg)' : 'var(--surface-2)',
            color: actionMeta.tone === 'green' ? 'var(--green)'
              : actionMeta.tone === 'amber' ? 'var(--amber)'
              : actionMeta.tone === 'purple' ? 'var(--purple)'
              : actionMeta.tone === 'red' ? 'var(--red)'
              : 'var(--text-3)',
          }}>
            {actionMeta.label}
          </span>
          <input
            value={nextAction}
            onChange={(e) => setNextAction(e.target.value)}
            placeholder="e.g. Send pricing + SOC2, follow up after trial"
            style={{
              flex: 1, minWidth: 220, padding: '7px 10px',
              borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)',
              fontSize: 12, background: 'var(--surface)', color: 'var(--text)',
            }}
          />
          <input
            type="date"
            value={nextActionDue}
            onChange={(e) => setNextActionDue(e.target.value)}
            style={{
              padding: '7px 10px', borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)', fontSize: 12,
              background: 'var(--surface)', color: 'var(--text)',
            }}
          />
          <button onClick={handleSaveNextAction} disabled={actionSaving} className="btn btn--ghost btn--sm">
            {actionSaving ? 'Saving…' : 'Save action'}
          </button>
        </div>
      </div>

      {/* Context tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <InfoTile label="Last outreach" value={r.days_since_last_outreach != null ? `${r.days_since_last_outreach} days ago` : 'Never'} />
        <InfoTile label="Stage" value={r.stage || '—'} />
        {r.last_outreach_subject && (
          <div style={{ gridColumn: '1 / -1' }}>
            <InfoTile label="Last subject" value={r.last_outreach_subject} />
          </div>
        )}
        {ampEntry && <InfoTile label="Amplitude" value={`${ampEntry.tier || '—'} (${ampEntry.score ?? '—'})`} />}
        {r.trial_end_date && <InfoTile label="Trial ends" value={r.trial_end_date} />}
        {snoozedUntil && <InfoTile label="Snoozed until" value={snoozedUntil} />}
      </div>

      {/* Email action */}
      <div>
        <SectionLabel>Actions</SectionLabel>

        {emailState === 'idle' && (
          <a
            href={mailto}
            onClick={() => setTimeout(() => setEmailState('sent'), 300)}
            className="btn btn--primary"
            style={{ display: 'inline-flex', marginBottom: 10, textDecoration: 'none' }}
          >
            Email {firstName || r.email}
          </a>
        )}

        {emailState === 'sent' && (
          <div className="card" style={{ padding: 14, marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
              Sent it? Log this touchpoint.
            </div>
            <textarea
              placeholder="Optional note..."
              value={logNote}
              onChange={e => setLogNote(e.target.value)}
              style={{
                width: '100%', height: 64, padding: '7px 10px',
                borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)',
                fontSize: 12, resize: 'none', boxSizing: 'border-box',
                background: 'var(--surface)', color: 'var(--text)',
                fontFamily: 'inherit',
              }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button onClick={handleLog} disabled={busy} className="btn btn--primary btn--sm">
                {busy ? 'Logging…' : 'Log it'}
              </button>
              <button onClick={() => setEmailState('idle')} className="btn btn--ghost btn--sm">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Secondary actions */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          {onOpenTimeline && (
            <button onClick={() => onOpenTimeline(r)} className="btn btn--ghost btn--sm">
              Timeline
            </button>
          )}
          <button onClick={() => setEmailState(s => s === 'emailed' ? 'idle' : 'emailed')} className="btn btn--ghost btn--sm">
            Already emailed
          </button>
          <button onClick={() => setShowSnooze(v => !v)} className="btn btn--ghost btn--sm">
            {snoozedUntil ? `Snoozed · ${snoozedUntil}` : 'Snooze'}
          </button>
          <button onClick={handleIgnore} disabled={busy} className="btn btn--ghost btn--sm">
            Ignore
          </button>
          <button onClick={() => setShowExclude(v => !v)} className="btn btn-danger btn--sm">
            Not my target
          </button>
        </div>

        {/* Already-emailed → requires a log note, then moves to Awaiting follow-up */}
        {emailState === 'emailed' && (
          <div className="card" style={{ padding: 14, marginTop: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
              What did you send? (logged, then moved to Awaiting follow-up)
            </div>
            <textarea
              placeholder="e.g. Sent intro + pricing deck"
              value={logNote}
              onChange={e => setLogNote(e.target.value)}
              style={{
                width: '100%', height: 60, padding: '7px 10px',
                borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)',
                fontSize: 12, resize: 'none', boxSizing: 'border-box',
                background: 'var(--surface)', color: 'var(--text)', fontFamily: 'inherit',
              }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button onClick={handleAlreadyEmailed} disabled={busy || !logNote.trim()} className="btn btn--primary btn--sm">
                {busy ? 'Logging…' : 'Log & move to follow-up'}
              </button>
              <button onClick={() => { setEmailState('idle'); setLogNote(''); }} className="btn btn--ghost btn--sm">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Snooze picker */}
        {showSnooze && (
          <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="date"
              value={snoozeDate}
              min={minDate.toISOString().slice(0, 10)}
              onChange={e => setSnoozeDate(e.target.value)}
              style={{
                padding: '5px 10px', borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)', fontSize: 13,
                background: 'var(--surface)', color: 'var(--text)',
              }}
            />
            <button onClick={handleSnooze} disabled={!snoozeDate} className="btn btn--primary btn--sm">
              Confirm
            </button>
            {snoozedUntil && (
              <button
                onClick={() => { snoozeContact(r.contact_id, null); onSnooze?.(r.contact_id); }}
                className="btn btn--ghost btn--sm"
              >
                Clear
              </button>
            )}
          </div>
        )}

        {/* Exclude form */}
        {showExclude && (
          <div style={{
            marginTop: 10,
            background: 'var(--red-bg)', border: '1px solid #fecaca',
            borderRadius: 'var(--radius)', padding: 14,
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--red)', marginBottom: 8 }}>
              Remove from outreach list
            </div>
            <input
              placeholder="Why? (e.g. not ICP, wrong role, wrong firm type…)"
              value={excludeReason}
              onChange={e => setExcludeReason(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleExclude()}
              style={{
                width: '100%', padding: '7px 10px',
                borderRadius: 'var(--radius-sm)', border: '1px solid #fca5a5',
                fontSize: 13, boxSizing: 'border-box',
                background: '#fff', color: 'var(--text)', fontFamily: 'inherit',
              }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button
                onClick={handleExclude}
                disabled={busy || !excludeReason.trim()}
                className="btn btn-danger btn--sm"
              >
                {busy ? 'Removing…' : 'Confirm'}
              </button>
              <button onClick={() => setShowExclude(false)} className="btn btn--ghost btn--sm">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {r.linkedin_url && (
        <a href={r.linkedin_url} target="_blank" rel="noopener noreferrer"
          style={{ fontSize: 12, color: 'var(--orange)', fontWeight: 500 }}>
          LinkedIn ↗
        </a>
      )}
    </div>
  );
}

// ── ICP Banner ────────────────────────────────────────────────────────────────

function IcpBanner({ icp, getToken, onSaved }) {
  const [editing, setEditing] = useState(!icp);
  const [draft, setDraft] = useState(icp || '');
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await updateConfig(getToken, { gtm_icp_definition: draft.trim() });
      onSaved(draft.trim());
      setEditing(false);
    } catch (e) {
      alert('Save failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  if (!editing) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '9px 14px',
        background: 'var(--surface-2)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        fontSize: 13,
      }}>
        <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--orange)', whiteSpace: 'nowrap' }}>ICP</span>
        <span style={{ flex: 1, color: 'var(--text-2)' }}>{icp}</span>
        <button onClick={() => { setDraft(icp); setEditing(true); }} className="btn btn--ghost btn--sm" style={{ flexShrink: 0 }}>
          Edit
        </button>
      </div>
    );
  }

  return (
    <div style={{
      padding: '14px 16px',
      background: 'var(--orange-light)', border: '1px solid var(--orange-mid)',
      borderRadius: 'var(--radius)',
    }}>
      <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--navy)', marginBottom: 8 }}>
        {icp ? 'Edit ICP definition' : 'Define your Ideal Customer Profile'}
      </div>
      <textarea
        value={draft}
        onChange={e => setDraft(e.target.value)}
        placeholder="e.g. Portfolio managers at hedge funds and mutual funds with $500M+ AUM who actively manage equities…"
        rows={3}
        style={{
          width: '100%', padding: '8px 10px',
          borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)',
          fontSize: 13, resize: 'vertical', boxSizing: 'border-box',
          background: 'var(--surface)', color: 'var(--text)', fontFamily: 'inherit',
        }}
      />
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button onClick={save} disabled={saving || !draft.trim()} className="btn btn--primary btn--sm">
          {saving ? 'Saving…' : 'Save'}
        </button>
        {icp && <button onClick={() => setEditing(false)} className="btn btn--ghost btn--sm">Cancel</button>}
      </div>
    </div>
  );
}

// ── AI filter bar ─────────────────────────────────────────────────────────────

function AiFilterBar({ onFilter, loading }) {
  const [query, setQuery] = useState('');

  function submit() {
    if (!query.trim()) return;
    onFilter(query.trim());
  }

  function clear() {
    setQuery('');
    onFilter('');
  }

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <div style={{ flex: 1, position: 'relative' }}>
        <span style={{
          position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
          fontSize: 12, color: 'var(--orange)', userSelect: 'none',
        }}>✦</span>
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && submit()}
          placeholder='AI filter: "hedge fund PMs only", "replied last month", "trial ending soon"…'
          style={{
            width: '100%', padding: '8px 10px 8px 28px',
            borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)',
            fontSize: 13, background: 'var(--surface)', color: 'var(--text)',
            boxSizing: 'border-box', fontFamily: 'inherit',
          }}
        />
      </div>
      <button onClick={submit} disabled={loading || !query.trim()} className="btn btn--primary btn--sm">
        {loading ? '…' : 'Filter'}
      </button>
      {query && (
        <button onClick={clear} className="btn btn--ghost btn--sm">Clear</button>
      )}
    </div>
  );
}

// ── Analytics (collapsible) ───────────────────────────────────────────────────

const SENTIMENT_COLOR = {
  positive:      { text: '#15803d', bg: '#dcfce7', border: '#86efac' },
  negative:      { text: '#dc2626', bg: '#fee2e2', border: '#fca5a5' },
  question:      { text: '#92400e', bg: '#fef3c7', border: '#fde68a' },
  neutral:       { text: '#64748b', bg: '#f1f5f9', border: '#cbd5e1' },
  unclassified:  { text: '#64748b', bg: '#f1f5f9', border: '#cbd5e1' },
};

function DrillPanel({ drill, onOpenTimeline, onClose }) {
  if (!drill) return null;
  const { label, loading, rows, error } = drill;
  return (
    <div style={{ marginTop: 12, border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'var(--surface-2, #f8fafc)', borderBottom: '1px solid var(--border)' }}>
        <span style={{ fontWeight: 600, fontSize: 12, color: 'var(--text-2)', flex: 1 }}>{label}</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', fontSize: 16, lineHeight: 1 }}>×</button>
      </div>
      {loading && <div style={{ padding: '14px 12px', color: 'var(--text-3)', fontSize: 12 }}>Loading…</div>}
      {error   && <div style={{ padding: '14px 12px', color: '#dc2626', fontSize: 12 }}>{error}</div>}
      {!loading && !error && rows?.length === 0 && (
        <div style={{ padding: '14px 12px', color: 'var(--text-3)', fontSize: 12 }}>No contacts found.</div>
      )}
      {!loading && rows?.length > 0 && (
        <div style={{ maxHeight: 320, overflowY: 'auto' }}>
          {rows.map((r, i) => (
            <div key={r.contact_id || i} style={{
              display: 'flex', alignItems: 'baseline', gap: 8, padding: '7px 12px',
              borderBottom: i < rows.length - 1 ? '1px solid var(--border)' : 'none',
              background: i % 2 === 0 ? 'transparent' : 'var(--surface-2, #f9fafb)',
            }}>
              <button
                onClick={() => onOpenTimeline?.({ contact_id: r.contact_id, name: r.name, email: r.email, firm_name: r.firm_name, _firm_name: r.firm_name })}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left', fontWeight: 600, fontSize: 12, color: 'var(--text)', minWidth: 0 }}
              >
                {r.name || r.email || '—'}
              </button>
              {r.firm_name && <span style={{ fontSize: 11, color: 'var(--text-3)', flexShrink: 0 }}>{r.firm_name}</span>}
              <span style={{ flex: 1 }} />
              {r.days_in_stage != null && (
                <span style={{ fontSize: 10, color: 'var(--text-3)', flexShrink: 0 }}>{r.days_in_stage}d</span>
              )}
              {r.sent_count != null && (() => {
                const s = (r.latest_reply_sentiment || '').toLowerCase();
                const chip = LATEST_SENT_CHIP[s];
                if (chip) return (
                  <span style={{ fontSize: 9.5, fontWeight: 600, padding: '1px 7px', borderRadius: 8, background: chip.bg, color: chip.text, flexShrink: 0 }}>
                    {chip.label}
                  </span>
                );
                if (r.reply_count > 0) return (
                  <span style={{ fontSize: 10, color: '#15803d', flexShrink: 0 }}>Replied</span>
                );
                return null;
              })()}
              {r.last_ts && r.days_in_stage == null && r.sent_count == null && (
                <span style={{ fontSize: 10, color: 'var(--text-3)', flexShrink: 0 }}>
                  {new Date(r.last_ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const SENT_COLORS_OUT = {
  positive:     { bg: '#dcfce7', text: '#15803d' },
  negative:     { bg: '#fee2e2', text: '#dc2626' },
  question:     { bg: '#fef3c7', text: '#92400e' },
  neutral:      { bg: '#f1f5f9', text: '#475569' },
  ooo:          { bg: '#fef9c3', text: '#b45309' },
  bounce:       { bg: '#fef9c3', text: '#b45309' },
  unsubscribed: { bg: '#fce7f3', text: '#be185d' },
  unsubscribe:  { bg: '#fce7f3', text: '#be185d' },
  unclassified: { bg: '#f3f4f6', text: '#9ca3af' },
};
// Only actionable sentiments shown as quick-toggle options
const SENT_OPTIONS_LIST = ['positive', 'negative', 'question'];

function SentimentInboxRow({ row, getToken, onOpenTimeline, onDismiss }) {
  const [sent, setSent] = useState(row.reply_sentiment || 'unclassified');
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const chip = SENT_COLORS_OUT[sent] ?? SENT_COLORS_OUT.unclassified;
  const aiLabel = row.reply_sentiment_ai || null;
  const humanOverrode = aiLabel && aiLabel !== (row.reply_sentiment || 'unclassified') && sent === (row.reply_sentiment || 'unclassified');

  async function handleSentiment(newSent) {
    setSaving(true);
    try {
      const id = row.log_id || row.gmail_thread_id || row.bd_send_id;
      const key = row.log_id ? 'logId' : row.gmail_thread_id ? 'threadId' : 'logId';
      await patchReplySentiment(getToken, { [key]: id, sentiment: newSent === 'unclassified' ? '' : newSent });
      setSent(newSent);
    } catch (e) { console.error(e); }
    setSaving(false);
  }

  async function handleDismiss() {
    setSaving(true);
    try {
      const id = row.log_id || row.gmail_thread_id || row.bd_send_id;
      const key = row.log_id ? 'logId' : row.gmail_thread_id ? 'threadId' : 'logId';
      await patchReplySentiment(getToken, { [key]: id, sentiment: 'dismissed' });
      onDismiss?.();
    } catch (e) { console.error(e); setSaving(false); }
  }

  const date = row.event_ts ? new Date(row.event_ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—';
  const body = row.body_snippet?.replace(/\s+/g, ' ').trim() || '';
  const answered = !!row.answered;
  const latestReply = row.latest_reply_body_snippet?.replace(/\s+/g, ' ').trim() || '';
  const threadState = getThreadState(row);
  const [showThread, setShowThread] = useState(false);
  const [threadRows, setThreadRows] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState('');
  const [threadOwnerEmail, setThreadOwnerEmail] = useState('');

  async function loadThread() {
    if (threadRows || threadLoading) return;
    setThreadLoading(true);
    setThreadError('');
    try {
      const data = await fetchOutreachThread(getToken, row.gmail_thread_id);
      setThreadRows(data.rows ?? []);
      setThreadOwnerEmail((data.rows ?? []).find((r) => r.owner_email)?.owner_email || row.owner_email || '');
    } catch (e) {
      setThreadError(e.message || 'Failed to load thread');
    } finally {
      setThreadLoading(false);
    }
  }

  return (
    <div style={{ borderBottom: '1px solid var(--border)', padding: '9px 0' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        {/* Sentiment column */}
        <div style={{ flexShrink: 0, width: 108 }}>
          {/* Current label */}
          <div style={{
            fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 5, marginBottom: 4,
            background: chip.bg, color: chip.text, textTransform: 'capitalize', textAlign: 'center',
          }}>{sent}</div>
          {/* AI original if different */}
          {aiLabel && aiLabel !== sent && (
            <div style={{ fontSize: 9, color: 'var(--text-3)', textAlign: 'center', marginBottom: 3 }}>
              AI: <span style={{ color: (SENT_COLORS_OUT[aiLabel] ?? SENT_COLORS_OUT.unclassified).text, fontWeight: 600 }}>{aiLabel}</span>
            </div>
          )}
          {/* Override options */}
          <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {SENT_OPTIONS_LIST.filter(o => o !== sent).map(o => {
              const c = SENT_COLORS_OUT[o];
              return (
                <button key={o} disabled={saving} onClick={() => handleSentiment(o)}
                  style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, border: `1px solid ${c.bg}`,
                    background: 'transparent', color: c.text, cursor: 'pointer', opacity: saving ? 0.5 : 1 }}>
                  {o === 'unsubscribed' ? 'unsub' : o}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap', marginBottom: 1 }}>
            <button onClick={() => onOpenTimeline?.({ contact_id: row.contact_id, name: row.contact_name, email: row.email, firm_name: row.firm_name, _firm_name: row.firm_name })}
              style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              {row.contact_name || row.email}
            </button>
            {row.firm_name && <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{row.firm_name}</span>}
            <span style={{ fontSize: 10, color: 'var(--text-3)', marginLeft: 'auto' }}>{date}</span>
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 999,
              background: answered ? 'var(--blue-bg)' : 'var(--amber-bg)',
              color: answered ? 'var(--blue)' : 'var(--amber)',
              flexShrink: 0,
            }}>
              {answered ? 'Answered' : 'Unanswered'}
            </span>
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 999,
              background: threadState.tone === 'blue' ? 'var(--blue-bg)' : threadState.tone === 'green' ? 'var(--green-bg)' : threadState.tone === 'amber' ? 'var(--amber-bg)' : 'var(--surface-2)',
              color: threadState.tone === 'blue' ? 'var(--blue)' : threadState.tone === 'green' ? 'var(--green)' : threadState.tone === 'amber' ? 'var(--amber)' : 'var(--text-3)',
              flexShrink: 0,
            }}>
              {threadState.label}
            </span>
            <button disabled={saving} onClick={handleDismiss}
              style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, border: '1px solid var(--border)',
                background: 'transparent', color: 'var(--text-3)', cursor: 'pointer', opacity: saving ? 0.5 : 1 }}>
              dismiss
            </button>
          </div>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-2)', marginBottom: 3 }}>{row.subject}</div>
          {body ? (
            <div style={{ fontSize: 11, color: 'var(--text-3)', lineHeight: 1.45,
              background: 'var(--surface-2,#f8fafc)', borderRadius: 5, padding: '5px 8px',
              borderLeft: '2px solid var(--border)' }}>
              {expanded ? body : body.slice(0, 220)}
              {!expanded && body.length > 220 && (
                <>…<button onClick={() => setExpanded(true)} style={{ fontSize: 10, color: 'var(--orange)', background: 'none', border: 'none', cursor: 'pointer', marginLeft: 4 }}>show more</button></>
              )}
              {expanded && <button onClick={() => setExpanded(false)} style={{ fontSize: 10, color: 'var(--text-3)', background: 'none', border: 'none', cursor: 'pointer', marginLeft: 6 }}>collapse</button>}
            </div>
          ) : (
            <div style={{ fontSize: 10, color: 'var(--text-3)', fontStyle: 'italic' }}>No body stored — classify based on subject</div>
          )}
          {answered && latestReply && (
            <div style={{ marginTop: 6, fontSize: 11, lineHeight: 1.45, background: 'var(--blue-bg)', borderRadius: 5, padding: '5px 8px', borderLeft: '2px solid var(--blue)' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--blue)', marginBottom: 2 }}>
                Latest reply{row.latest_reply_owner_email ? ` · ${row.latest_reply_owner_email}` : ''}
              </div>
              {row.latest_reply_subject ? <div style={{ marginBottom: 2, fontSize: 10.5, color: 'var(--text-2)' }}>{row.latest_reply_subject}</div> : null}
              <div style={{ color: 'var(--text-2)' }}>{latestReply}</div>
            </div>
          )}
          <div style={{ marginTop: 6 }}>
            <button
              onClick={async () => {
                const next = !showThread;
                setShowThread(next);
                if (next) await loadThread();
              }}
              style={{ fontSize: 10, color: 'var(--orange)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              {showThread ? 'Hide thread context' : 'Show full thread context'}
            </button>
          </div>
          {showThread && (
            <div style={{ marginTop: 8, border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', background: 'var(--surface)' }}>
              <div style={{ padding: '8px 10px', background: 'var(--surface-2)', borderBottom: '1px solid var(--border)', fontSize: 11, fontWeight: 700, color: 'var(--text-2)' }}>
                Thread Timeline
              </div>
              {threadLoading && <div style={{ padding: 10, fontSize: 12, color: 'var(--text-3)' }}>Loading thread…</div>}
              {threadError && <div style={{ padding: 10, fontSize: 12, color: '#dc2626' }}>{threadError}</div>}
              {!threadLoading && !threadError && (threadRows ?? []).length > 0 && (() => {
                const rows = threadRows ?? [];
                const outboundRows = rows.filter((m) => String(m.direction || '').toLowerCase() === 'outbound');
                const inboundRows = rows.filter((m) => String(m.direction || '').toLowerCase() !== 'outbound');
                const renderRow = (m, idx, total) => (
                  <div key={m.log_id || idx} style={{ padding: '8px 10px', borderBottom: idx < total - 1 ? '1px solid var(--border)' : 'none', background: idx % 2 ? 'var(--surface-2)' : 'transparent' }}>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline', marginBottom: 4 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 999, background: m.direction === 'Outbound' ? 'var(--blue-bg)' : 'var(--amber-bg)', color: m.direction === 'Outbound' ? 'var(--blue)' : 'var(--amber)' }}>
                        {m.direction || 'Unknown'}
                      </span>
                      <span style={{ fontSize: 10, color: 'var(--text-3)' }}>
                        {m.from_email
                          ? `${m.from_email}${m.to_emails?.length ? ` → ${m.to_emails.join(', ')}` : ''}`
                          : (m.owner_email || m.contact_name || m.email || '—')}
                      </span>
                      <span style={{ fontSize: 10, color: 'var(--text-3)', marginLeft: 'auto' }}>{m.event_ts ? new Date(m.event_ts).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}</span>
                    </div>
                    {m.owner_email ? (
                      <div style={{ fontSize: 10, color: 'var(--text-3)', marginBottom: 3 }}>
                        Owner: {m.owner_email}
                      </div>
                    ) : null}
                    {m.subject ? <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-2)', marginBottom: 3 }}>{m.subject}</div> : null}
                    {m.body ? <div style={{ fontSize: 11, lineHeight: 1.45, color: 'var(--text-2)', whiteSpace: 'pre-wrap' }}>{m.body}</div> : <div style={{ fontSize: 11, color: 'var(--text-3)', fontStyle: 'italic' }}>No body stored</div>}
                  </div>
                );

                return (
                  <>
                    <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)', fontSize: 10, fontWeight: 700, color: 'var(--blue)' }}>
                      My sent replies {outboundRows.length ? `(${outboundRows.length})` : '(0)'}
                      {threadOwnerEmail ? <span style={{ color: 'var(--text-3)', fontWeight: 600 }}> · owner {threadOwnerEmail}</span> : null}
                    </div>
                    {outboundRows.length > 0
                      ? outboundRows.map((m, idx) => renderRow(m, idx, outboundRows.length))
                      : <div style={{ padding: 10, fontSize: 11, color: 'var(--text-3)' }}>No outbound reply detected in this thread.</div>}
                    <div style={{ padding: '8px 10px', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)', fontSize: 10, fontWeight: 700, color: 'var(--amber)' }}>
                      Their messages {inboundRows.length ? `(${inboundRows.length})` : '(0)'}
                    </div>
                    {inboundRows.length > 0
                      ? inboundRows.map((m, idx) => renderRow(m, idx, inboundRows.length))
                      : <div style={{ padding: 10, fontSize: 11, color: 'var(--text-3)' }}>No inbound messages found.</div>}
                  </>
                );
              })()}
              {!threadLoading && !threadError && threadRows?.length === 0 && <div style={{ padding: 10, fontSize: 12, color: 'var(--text-3)' }}>No thread messages found.</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AnalyticsCollapsible({ getToken, defaultOpen = false, hideToggle = false, onOpenTimeline }) {
  const [open, setOpen] = useState(defaultOpen);
  const [loaded, setLoaded] = useState(false);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('weekly');
  const [drill, setDrill] = useState(null); // {label, loading, rows, error}
  const [inbox, setInbox] = useState(null);
  const [inboxFilter, setInboxFilter] = useState('');
  const [sentimentFilter, setSentimentFilter] = useState('');
  const [answeredFilter, setAnsweredFilter] = useState('');
  const [inboxLoading, setInboxLoading] = useState(false);
  const [inboxError, setInboxError] = useState(null);
  const [backfillStatus, setBackfillStatus] = useState(null);
  const [reclassifyStatus, setReclassifyStatus] = useState(null);
  const [dedupStatus, setDedupStatus] = useState(null);

  useEffect(() => {
    if ((open || defaultOpen) && !loaded) {
      setLoaded(true);
      fetchOutreachAnalytics(getToken, 90).then(setData).catch(console.error);
    }
  }, [open, defaultOpen]);

  useEffect(() => {
    function onRefresh() {
      if (tab === 'review' || tab === 'weekly' || tab === 'sentiment' || tab === 'funnel' || tab === 'batch' || tab === 'analytics') {
        setInbox(null);
        setInboxLoading(false);
        loadInbox?.();
      }
      setData(null);
      fetchOutreachAnalytics(getToken, 90).then(setData).catch(console.error);
    }
    function onStorage(ev) {
      if (ev.key === 'salesdash:outreach-refresh') onRefresh();
    }
    window.addEventListener('salesdash:outreach-refresh', onRefresh);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('salesdash:outreach-refresh', onRefresh);
      window.removeEventListener('storage', onStorage);
    };
  }, [getToken, tab]);

  // Clear drill panel when switching tabs
  useEffect(() => { setDrill(null); }, [tab]);

  // Load sentiment inbox when review tab is opened
  function loadInbox() {
    setInboxLoading(true);
    setInboxError(null);
    fetchSentimentInbox(getToken, { days: 180 })
      .then(r => setInbox(r.rows))
      .catch(e => setInboxError(e.message || 'Failed to load'))
      .finally(() => setInboxLoading(false));
  }
  useEffect(() => {
    if (tab === 'review' && !inbox && !inboxLoading) loadInbox();
  }, [tab]);

  function openDrill(type, value, label) {
    setDrill({ label, loading: true, rows: null, error: null });
    fetchAnalyticsDrill(getToken, type, value, 90)
      .then((res) => setDrill((d) => d ? { ...d, loading: false, rows: res.rows } : d))
      .catch((e) => setDrill((d) => d ? { ...d, loading: false, error: e.message } : d));
  }

  const thS = { textAlign: 'left', padding: '4px 8px', fontWeight: 600, color: 'var(--text-2)', fontSize: 11 };
  const tdS = { padding: '5px 8px', fontSize: 12, color: 'var(--text)' };

  function renderTab() {
    if (!data) return <div style={{ color: 'var(--text-3)', fontSize: 13 }}>Loading…</div>;

    if (tab === 'weekly') {
      const rows = data.weekly ?? [];
      if (!rows.length) return <div style={{ color: 'var(--text-3)', fontSize: 13 }}>No data yet.</div>;
      const activeDrill = drill?.label?.startsWith('Week');
      return (
        <>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ borderBottom: '1px solid var(--border)' }}>
              <th style={thS}>Week of</th><th style={thS}>Sent</th><th style={thS}>Replies</th><th style={thS}>Reply %</th><th style={thS}>Positive</th><th style={thS}>Unsub</th>
            </tr></thead>
            <tbody>{rows.slice(0, 12).map((r, i) => {
              const isActive = drill?.label === `Week ${r.week}`;
              const weekLabel = r.week_date
                ? new Date(r.week_date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                : r.week;
              return (
                <tr key={i}
                  onClick={() => openDrill('weekly', r.week, `Week ${r.week}`)}
                  style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer', background: isActive ? 'var(--surface-2,#f1f5f9)' : undefined }}
                >
                  <td style={{ ...tdS, fontWeight: 600 }}>{weekLabel}</td>
                  <td style={tdS}>{r.sent}</td>
                  <td style={{ ...tdS, color: r.replies > 0 ? '#15803d' : undefined }}>{r.replies}</td>
                  <td style={{ ...tdS, fontWeight: r.reply_rate_pct >= 20 ? 700 : 400, color: r.reply_rate_pct >= 20 ? '#15803d' : r.reply_rate_pct >= 10 ? 'var(--text)' : 'var(--text-3)' }}>
                    {r.reply_rate_pct != null ? `${r.reply_rate_pct}%` : '—'}
                  </td>
                  <td style={{ ...tdS, color: r.positive > 0 ? '#15803d' : undefined, fontWeight: r.positive > 0 ? 600 : 400 }}>{r.positive}</td>
                  <td style={{ ...tdS, color: r.unsubs > 0 ? '#dc2626' : undefined }}>{r.unsubs || '—'}</td>
                </tr>
              );
            })}</tbody>
          </table>
          <DrillPanel drill={drill} onOpenTimeline={onOpenTimeline} onClose={() => setDrill(null)} />
        </>
      );
    }

    if (tab === 'sentiment') {
      const rows = data.sentiment ?? [];
      return (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {rows.map((r, i) => {
              const s = SENTIMENT_COLOR[r.sentiment] ?? SENTIMENT_COLOR.neutral;
              const isActive = drill?.label === r.sentiment;
              const isUnsub = r.sentiment === 'unsubscribed';
              return (
                <button key={i} onClick={() => openDrill(isUnsub ? 'unsub' : 'sentiment', r.sentiment, r.sentiment)}
                  style={{
                    flex: '1 1 90px', padding: '12px 10px', textAlign: 'center', cursor: 'pointer',
                    border: `1px solid ${isActive ? s.border : 'var(--border)'}`,
                    borderRadius: 8, background: isActive ? s.bg : 'var(--surface)',
                    outline: 'none', transition: 'background 0.1s',
                  }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: s.text }}>{r.count}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2, textTransform: 'capitalize' }}>{r.sentiment}</div>
                </button>
              );
            })}
          </div>
          {!rows.length && <div style={{ color: 'var(--text-3)', fontSize: 13, marginTop: 8 }}>No reply data yet.</div>}
          <DrillPanel drill={drill} onOpenTimeline={onOpenTimeline} onClose={() => setDrill(null)} />
        </>
      );
    }

    if (tab === 'funnel') {
      const rows = data.funnel ?? [];
      return (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {rows.map((r, i) => {
              const isActive = drill?.label === r.stage;
              return (
                <div key={i} onClick={() => openDrill('funnel', r.stage, r.stage)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', borderRadius: 6, padding: '3px 0', background: isActive ? 'var(--surface-2,#f1f5f9)' : undefined }}>
                  <div style={{ width: 80, fontSize: 11, color: 'var(--text-3)', textAlign: 'right', flexShrink: 0, textTransform: 'capitalize' }}>{r.stage || '—'}</div>
                  <div style={{ flex: 1, height: 12, background: 'var(--border)', borderRadius: 999, overflow: 'hidden' }}>
                    <div style={{ width: `${Math.min(100, (r.count / (rows[0]?.count || 1)) * 100)}%`, height: '100%', background: isActive ? 'var(--orange)' : 'var(--orange)', opacity: isActive ? 1 : 0.7, borderRadius: 999 }} />
                  </div>
                  <div style={{ width: 28, fontSize: 11, fontWeight: 600, color: 'var(--text-2)', textAlign: 'right' }}>{r.count}</div>
                  {r.avg_days_in_stage != null && (
                    <div style={{ width: 55, fontSize: 10, color: 'var(--text-3)' }}>{Math.round(r.avg_days_in_stage)}d avg</div>
                  )}
                </div>
              );
            })}
            {!rows.length && <div style={{ color: 'var(--text-3)', fontSize: 13 }}>No stage data yet.</div>}
          </div>
          <DrillPanel drill={drill} onOpenTimeline={onOpenTimeline} onClose={() => setDrill(null)} />
        </>
      );
    }

    if (tab === 'batch') {
      const rows = data.batches ?? [];
      return (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ borderBottom: '1px solid var(--border)' }}>
            <th style={thS}>Subject / Batch</th><th style={thS}>Date</th><th style={thS}>Sent</th><th style={thS}>Reply %</th><th style={thS}>Positive</th>
          </tr></thead>
          <tbody>{rows.map((r, i) => (
            <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
              <td style={{ ...tdS, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.subject}>{r.subject || '—'}</td>
              <td style={tdS}>{r.day}</td>
              <td style={tdS}>{r.sent}</td>
              <td style={{ ...tdS, fontWeight: r.reply_rate > 0.1 ? 600 : 400, color: r.reply_rate > 0.1 ? '#15803d' : undefined }}>
                {r.sent > 0 ? `${Math.round(r.reply_rate * 100)}%` : '—'}
              </td>
              <td style={tdS}>{r.positive_replies}</td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={5} style={{ ...tdS, color: 'var(--text-3)', textAlign: 'center', padding: 12 }}>No batch data yet (min 5 sends per subject).</td></tr>}
          </tbody>
        </table>
      );
    }

    if (tab === 'analytics') {
      const weekly = data.weekly ?? [];
      const sentiment = data.sentiment ?? [];
      const funnel = data.funnel ?? [];
      const batches = data.batches ?? [];
      const topWeek = weekly[0] ?? {};
      const sentTop = sentiment.reduce((acc, r) => (Number(r.count ?? 0) > Number(acc.count ?? 0) ? r : acc), sentiment[0] ?? {});
      const hotStage = funnel.find((r) => String(r.stage ?? '').toLowerCase() === 'hot') ?? funnel[0] ?? {};
      return (
        <div style={{ display: 'grid', gap: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, background: 'var(--surface)' }}>
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 4 }}>Last week sent</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--navy)' }}>{topWeek.sent ?? '—'}</div>
              <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{topWeek.week ? `Week ${topWeek.week}` : 'No data yet'}</div>
            </div>
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, background: 'var(--surface)' }}>
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 4 }}>Top sentiment</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--navy)' }}>{sentTop.sentiment ?? '—'}</div>
              <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{sentTop.count != null ? `${sentTop.count} rows` : 'No data yet'}</div>
            </div>
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, background: 'var(--surface)' }}>
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 4 }}>Hot funnel stage</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--navy)' }}>{hotStage.stage ?? '—'}</div>
              <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{hotStage.count != null ? `${hotStage.count} contacts` : 'No data yet'}</div>
            </div>
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, background: 'var(--surface)' }}>
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 4 }}>Batch subjects</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--navy)' }}>{batches.length}</div>
              <div style={{ fontSize: 11, color: 'var(--text-3)' }}>Tracked batch groups</div>
            </div>
          </div>
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)' }}>
            <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)', fontSize: 12, fontWeight: 700 }}>Recent weekly summary</div>
            <div style={{ padding: 12 }}>
              {weekly.slice(0, 5).map((r, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '6px 0', borderBottom: i < 4 ? '1px solid var(--border)' : 'none' }}>
                  <span style={{ fontSize: 12, color: 'var(--text-2)' }}>{r.week_date || r.week || '—'}</span>
                  <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{r.sent ?? 0} sent · {r.replies ?? 0} replies · {r.reply_rate_pct != null ? `${r.reply_rate_pct}%` : '—'}</span>
                </div>
              ))}
              {weekly.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>No weekly analytics yet.</div>}
            </div>
          </div>
        </div>
      );
    }

    if (tab === 'review') {
      // Two independent axes — sentiment category and answered-status — combine
      // with AND, so e.g. "unanswered" + "ooo/bounced" narrows to just that
      // intersection instead of one overloaded filter mixing everything together.
      const filtered = (inbox ?? []).filter(r => {
        const sentiment = r.reply_sentiment || 'unclassified';
        if (sentimentFilter === '__ooo_bounce__') {
          if (!['ooo', 'bounce'].includes(sentiment)) return false;
        } else if (sentimentFilter && sentiment !== sentimentFilter) {
          return false;
        }
        if (answeredFilter === 'answered' && !r.answered) return false;
        if (answeredFilter === 'unanswered' && r.answered) return false;
        if (inboxFilter) {
          const f = inboxFilter.toLowerCase();
          const matchesText = (r.contact_name || '').toLowerCase().includes(f)
            || (r.firm_name || '').toLowerCase().includes(f)
            || (r.subject || '').toLowerCase().includes(f);
          if (!matchesText) return false;
        }
        return true;
      });
      const counts = { total: inbox?.length ?? 0 };
      if (inbox) {
        for (const r of inbox) {
          const s = r.reply_sentiment || 'unclassified';
          counts[s] = (counts[s] ?? 0) + 1;
          if (r.answered) counts.answered = (counts.answered ?? 0) + 1;
          else counts.unanswered = (counts.unanswered ?? 0) + 1;
        }
      }
      const oooBounceCount = (counts.ooo ?? 0) + (counts.bounce ?? 0);
      return (
        <div>
          {/* Summary chips */}
          {inbox && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {[
                ['all', counts.total, ''],
                ['positive', counts.positive ?? 0, 'positive'],
                ['negative', counts.negative ?? 0, 'negative'],
                ['question', counts.question ?? 0, 'question'],
                ['unsubscribed', counts.unsubscribed ?? 0, 'unsubscribed'],
                ['ooo/bounced', oooBounceCount, '__ooo_bounce__'],
                ['unclassified', counts.unclassified ?? 0, 'unclassified'],
              ].map(([label, count, filterVal]) => {
                const colorKey = filterVal === '__ooo_bounce__' ? 'ooo' : label;
                const c = SENT_COLORS_OUT[colorKey] ?? { bg: 'var(--surface-2)', text: 'var(--text-2)' };
                return (
                  <button key={label} onClick={() => setSentimentFilter(filterVal)}
                    style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                      background: sentimentFilter === filterVal ? c.bg : 'var(--surface-2)',
                      color: sentimentFilter === filterVal ? c.text : 'var(--text-3)',
                      border: '1px solid var(--border)', cursor: 'pointer' }}>
                    {label} {count}
                  </button>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              {[['all statuses', '', counts.total], ['unanswered', 'unanswered', counts.unanswered ?? 0], ['answered', 'answered', counts.answered ?? 0]].map(([label, filterVal, count]) => (
                <button key={label} onClick={() => setAnsweredFilter(filterVal)}
                  style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                    background: answeredFilter === filterVal ? 'var(--text)' : 'var(--surface-2)',
                    color: answeredFilter === filterVal ? 'var(--surface)' : 'var(--text-3)',
                    border: '1px solid var(--border)', cursor: 'pointer' }}>
                  {label} {count}
                </button>
              ))}
              <button onClick={() => { setInbox(null); loadInbox(); }}
                style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: 'none', border: '1px solid var(--border)', cursor: 'pointer', color: 'var(--text-3)', marginLeft: 'auto' }}>
                ↺ Refresh
              </button>
              <button
                disabled={backfillStatus === 'loading'}
                onClick={() => {
                  setBackfillStatus('loading');
                  backfillGmailBodies(getToken, { days: 180, limit: 500 })
                    .then(r => { setBackfillStatus(r); setInbox(null); loadInbox(); })
                    .catch(e => setBackfillStatus({ error: e.message }));
                }}
                style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: 'none', border: '1px solid var(--border)', cursor: backfillStatus === 'loading' ? 'wait' : 'pointer', color: backfillStatus?.error ? '#dc2626' : 'var(--text-3)', marginLeft: 4 }}>
                {backfillStatus === 'loading' ? 'Fetching…' : backfillStatus?.error ? `✗ ${backfillStatus.error}` : backfillStatus?.updated != null ? `✓ ${backfillStatus.updated} fetched / ${backfillStatus.total ?? '?'} total / ${backfillStatus.skipped ?? 0} skipped${backfillStatus.cleared ? `, ${backfillStatus.cleared} cleared` : ''}` : '⬇ Fetch Bodies'}
              </button>
              <button
                disabled={reclassifyStatus === 'loading'}
                onClick={() => {
                  setReclassifyStatus('loading');
                  reclassifyAllSentiment(getToken, { days: 365, force: true })
                    .then(r => { setReclassifyStatus(r); setInbox(null); loadInbox(); })
                    .catch(e => setReclassifyStatus({ error: e.message }));
                }}
                style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: 'none', border: '1px solid var(--border)', cursor: reclassifyStatus === 'loading' ? 'wait' : 'pointer', color: reclassifyStatus?.error ? '#dc2626' : 'var(--text-3)', marginLeft: 4 }}>
                {reclassifyStatus === 'loading' ? '🤖 Classifying…' : reclassifyStatus?.error ? `✗ ${reclassifyStatus.error}` : reclassifyStatus?.classified != null ? `✓ ${reclassifyStatus.classified} classified (${reclassifyStatus.no_body} skipped)` : '🤖 Reclassify All'}
              </button>
              <button
                disabled={dedupStatus === 'loading'}
                onClick={() => {
                  setDedupStatus('loading');
                  dedupOutreachLog(getToken)
                    .then(r => { setDedupStatus(r); setInbox(null); loadInbox(); })
                    .catch(e => setDedupStatus({ error: e.message }));
                }}
                style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: 'none', border: '1px solid var(--border)', cursor: dedupStatus === 'loading' ? 'wait' : 'pointer', color: dedupStatus?.error ? '#dc2626' : 'var(--text-3)', marginLeft: 4 }}>
                {dedupStatus === 'loading' ? 'Deduping…' : dedupStatus?.error ? `✗ ${dedupStatus.error}` : dedupStatus?.removed != null ? `✓ ${dedupStatus.removed} removed` : '🧹 Dedup'}
              </button>
            </div>
            </div>
          )}
          {/* Search */}
          <input
            placeholder="Search contact, firm, subject…"
            value={inboxFilter}
            onChange={e => setInboxFilter(e.target.value)}
            style={{ width: '100%', padding: '5px 9px', fontSize: 12, borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', boxSizing: 'border-box', marginBottom: 8 }}
          />
          {inboxLoading && <div style={{ color: 'var(--text-3)', fontSize: 13, padding: '10px 0' }}>Loading emails…</div>}
          {inboxError && (
            <div style={{ color: '#dc2626', fontSize: 12, padding: '10px 0' }}>
              Error: {inboxError}
              <button onClick={loadInbox} style={{ marginLeft: 10, fontSize: 11, color: 'var(--orange)', background: 'none', border: 'none', cursor: 'pointer' }}>Retry</button>
            </div>
          )}
          {!inboxLoading && !inboxError && inbox && filtered.length === 0 && (
            <div style={{ color: 'var(--text-3)', fontSize: 13, padding: '10px 0' }}>No emails match.</div>
          )}
          {!inboxLoading && filtered
            .slice()
            .sort((a, b) => {
              if (!!a.answered !== !!b.answered) return a.answered ? 1 : -1;
              return new Date(b.event_ts || 0).getTime() - new Date(a.event_ts || 0).getTime();
            })
            .slice(0, 200)
            .map((row, i) => (
              <SentimentInboxRow key={row.log_id || i} row={row} getToken={getToken} onOpenTimeline={onOpenTimeline}
                onDismiss={() => setInbox(prev => prev.filter(r => r.log_id !== row.log_id))} />
            ))}
          {filtered.length > 200 && <div style={{ fontSize: 11, color: 'var(--text-3)', padding: '8px 0' }}>Showing 200 of {filtered.length} — use filter to narrow down</div>}
        </div>
      );
    }

    return null;
  }

  return (
    <div>
      {!hideToggle && (
        <button
          onClick={() => setOpen(v => !v)}
          style={{
            width: '100%', padding: '10px 16px',
            background: 'none', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 8,
            fontSize: 13, fontWeight: 600, color: 'var(--text-2)',
            textAlign: 'left',
          }}
        >
          <span style={{ fontSize: 9, color: 'var(--text-3)' }}>{open ? '▼' : '▶'}</span>
          Analytics
        </button>
      )}

      {open && (
        <div style={{ padding: '0 16px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {[['weekly','Weekly'],['sentiment','Sentiment'],['funnel','Funnel'],['batch','Batches'],['review','📥 Review'],['analytics','Analytics']].map(([t, label]) => (
              <button key={t} onClick={() => setTab(t)}
                className={`btn btn--sm ${tab === t ? 'btn--primary' : 'btn--ghost'}`}
                style={{ fontSize: 11 }}>
                {label}
              </button>
            ))}
          </div>
          {renderTab()}
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

// ── AI deal follow-up queue ─────────────────────────────────────────────────
// Contacts with deal_tier ∈ {Hot, Warm} and an open next_step, ordered by score.
// Replaces relying on the coarse warm/cold stage for prioritization (spec §10).
const FOLLOWUP_TIER_BADGE = {
  Hot:  { bg: '#fee2e2', fg: '#b91c1c' },
  Warm: { bg: '#ffedd5', fg: '#c2410c' },
};

function FollowUpQueue({ getToken, onOpenTimeline }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    let alive = true;
    fetchContactsPage(getToken, { limit: 400, sort: 'deal_likelihood', dir: 'desc' })
      .then((res) => {
        if (!alive) return;
        const list = (res.rows ?? [])
          .filter((c) => ['Hot', 'Warm'].includes(c.deal_tier) && (c.deal_next_step ?? '').trim())
          .slice(0, 50);
        setRows(list);
      })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [getToken]);

  if (rows == null || rows.length === 0) return null;

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)', overflow: 'hidden' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 12px',
          fontSize: 12, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase',
          color: 'var(--orange)', background: 'var(--surface-2)', border: 'none', cursor: 'pointer', textAlign: 'left' }}
      >
        <span style={{ fontSize: 9, color: 'var(--text-3)' }}>{open ? '▼' : '▶'}</span>
        ✦ Follow-up queue · {rows.length}
      </button>
      {open && (
        <div style={{ maxHeight: 260, overflowY: 'auto' }}>
          {rows.map((c) => {
            const badge = FOLLOWUP_TIER_BADGE[c.deal_tier] ?? FOLLOWUP_TIER_BADGE.Warm;
            return (
              <div
                key={c.contact_id}
                onClick={() => onOpenTimeline?.(c)}
                style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 12px',
                  borderTop: '1px solid var(--border)', cursor: onOpenTimeline ? 'pointer' : 'default',
                  opacity: c.deal_confidence === 'low' ? 0.7 : 1 }}
              >
                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 5, background: badge.bg, color: badge.fg, flexShrink: 0, marginTop: 1 }}>
                  {c.deal_tier}{c.deal_trend === 'up' ? ' ↑' : c.deal_trend === 'down' ? ' ↓' : ''}
                </span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)' }}>
                    {c.name}{c._firm_name ? <span style={{ color: 'var(--text-3)', fontWeight: 400 }}> · {c._firm_name}</span> : null}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 1 }}>{c.deal_next_step}</div>
                </div>
                <span style={{ fontSize: 11, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', flexShrink: 0, marginTop: 2 }}>
                  {Math.round(c.deal_likelihood ?? 0)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Outreach({ getToken, data, onOpenTimeline, amplitudeScores }) {
  const [queue, setQueue] = useState([]);
  const [bdMap, setBdMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [loggedIds, setLoggedIds] = useState(new Set());
  const [excludedIds, setExcludedIds] = useState(new Set());
  const [filterChip, setFilterChip] = useState('all');
  const [sortBy, setSortBy] = useState('priority');
  const [aiQuery, setAiQuery] = useState('');
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [icp, setIcp] = useState('');
  const [icpLoaded, setIcpLoaded] = useState(false);
  const [, tick] = useState(0); // force re-render on snooze changes
  const [collapsedGroups, setCollapsedGroups] = useState({ queue: true });
  const [icpRank, setIcpRank] = useState(null);   // AI-ordered contact_ids for the ICP lane
  const [icpRanking, setIcpRanking] = useState(false);
  const [activeTab, setActiveTab] = useState('queue'); // 'queue' | 'cadence'
  const { trials = [] } = data ?? {};

  const loadQueue = useCallback(async () => {
    setLoading(true);
    try {
      const [qRes, bdRes, cfgRes] = await Promise.all([
        fetchOutreachQueue(getToken, { limit: 200 }),
        fetchBdHighIntent(getToken, 21),
        fetchConfig(getToken),
      ]);

      const map = {};
      for (const row of (bdRes.rows ?? bdRes ?? [])) {
        if (row.email && !map[row.email]) map[row.email] = row;
      }
      setBdMap(map);

      const enriched = (qRes.rows ?? []).map(r => ({
        ...r,
        _priority: computePriority(r, amplitudeScores?.[r.email], map[r.email]),
      }));
      setQueue(enriched);

      if (cfgRes.gtm_icp_definition) setIcp(cfgRes.gtm_icp_definition);
      setIcpLoaded(true);
    } catch (e) {
      console.error('[Outreach] load error', e);
    } finally {
      setLoading(false);
    }
  }, [getToken, amplitudeScores]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  useEffect(() => {
    function onRefresh() { loadQueue(); }
    function onStorage(ev) {
      if (ev.key === 'salesdash:outreach-refresh') loadQueue();
    }
    window.addEventListener('salesdash:outreach-refresh', onRefresh);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('salesdash:outreach-refresh', onRefresh);
      window.removeEventListener('storage', onStorage);
    };
  }, [loadQueue]);

  // Scroll the selected row to the top of the left list so it lines up with the
  // detail panel on the right (which fills from the top when a contact is open).
  useEffect(() => {
    if (!selected) return;
    const el = document.getElementById(`orow-${selected}`);
    if (el) el.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [selected]);

  async function handleAiFilter(query) {
    if (!query) { setAiResult(null); setAiQuery(''); return; }
    setAiQuery(query);
    setAiLoading(true);
    try {
      const payload = queue
        .filter(r => !excludedIds.has(r.contact_id) && !isSnoozed(r.contact_id))
        .map(r => ({
          contact_id: r.contact_id, name: r.name, firm_name: r.firm_name,
          role: r.role, sector_coverage: r.sector_coverage,
          institution_type: r.institution_type, stage: r.stage, is_trial: r.is_trial,
        }));
      const res = await aiFilterOutreach(getToken, query, payload, icp);
      setAiResult(res);
    } catch (e) {
      alert('AI filter failed: ' + e.message);
    } finally {
      setAiLoading(false);
    }
  }

  // AI tiebreak for the ICP lane: re-rank the heuristic-selected cold prospects
  // against the written ICP definition (hybrid = heuristic gate + AI ordering).
  async function handleIcpAiRank(candidates) {
    if (!candidates.length || icpRanking) return;
    setIcpRanking(true);
    try {
      const payload = candidates.map(r => ({
        contact_id: r.contact_id, name: r.name, firm_name: r.firm_name,
        role: r.role, sector_coverage: r.sector_coverage,
        institution_type: r.institution_type, stage: r.stage, is_trial: r.is_trial,
      }));
      const res = await aiFilterOutreach(
        getToken,
        'Rank strictly by ICP fit — who is most likely to reply to a cold email, start a trial, and become a paying customer. Best first.',
        payload,
        icp,
      );
      setIcpRank(res.contact_ids ?? []);
    } catch (e) {
      alert('AI rank failed: ' + e.message);
    } finally {
      setIcpRanking(false);
    }
  }

  const visible = queue.filter(r => {
    if (excludedIds.has(r.contact_id)) return false;
    if (loggedIds.has(r.contact_id)) return false;
    if (isSnoozed(r.contact_id)) return false;
    if (aiResult && aiQuery) return aiResult.contact_ids.includes(r.contact_id);
    if (filterChip === 'incomplete') return r.profile_completeness != null && r.profile_completeness < 70;
    if (LANE_ORDER.includes(filterChip)) return assignLane(r, amplitudeScores?.[r.email], bdMap[r.email]) === filterChip;
    return true;
  }).sort((a, b) => {
    if (sortBy === 'priority') return (b._priority ?? 0) - (a._priority ?? 0);
    if (sortBy === 'days') return (b.days_since_last_outreach ?? 0) - (a.days_since_last_outreach ?? 0);
    if (sortBy === 'trial') {
      if (!a.trial_end_date && !b.trial_end_date) return 0;
      if (!a.trial_end_date) return 1;
      if (!b.trial_end_date) return -1;
      return a.trial_end_date < b.trial_end_date ? -1 : 1;
    }
    return 0;
  });

  const orderedVisible = (aiResult && aiQuery)
    ? aiResult.contact_ids.map(id => visible.find(r => r.contact_id === id)).filter(Boolean)
    : visible;

  const laneOf = (r) => assignLane(r, amplitudeScores?.[r.email], bdMap[r.email]);
  const groups = LANE_ORDER.reduce((acc, id) => {
    acc[id] = orderedVisible.filter((r) => laneOf(r) === id);
    return acc;
  }, {});
  // Apply AI tiebreak order to the ICP lane if the rep ran it.
  if (icpRank && groups.icp.length) {
    const idx = new Map(icpRank.map((id, i) => [id, i]));
    groups.icp = [...groups.icp].sort((a, b) => (idx.get(a.contact_id) ?? 1e9) - (idx.get(b.contact_id) ?? 1e9));
  }

  const selectedRow = selected ? queue.find(r => r.contact_id === selected) : null;

  function handleLogged(id) { setLoggedIds(s => new Set([...s, id])); setSelected(null); }
  function handleExcluded(id) { setExcludedIds(s => new Set([...s, id])); setSelected(null); }
  function handleIgnored(id) { setExcludedIds(s => new Set([...s, id])); setSelected(null); }
  function handleSnooze() { tick(n => n + 1); setSelected(null); }
  function handleContactUpdate(id, fields) {
    setQueue((rows) => rows.map((row) => (row.contact_id === id ? { ...row, ...fields } : row)));
  }

  const activeRows = queue.filter(r => !excludedIds.has(r.contact_id) && !isSnoozed(r.contact_id) && !loggedIds.has(r.contact_id));
  const activeTotal = activeRows.length;
  const incompleteCount = activeRows.filter(r => r.profile_completeness != null && r.profile_completeness < 70).length;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--main-bg)' }}>

      {/* Top toolbar */}
      <div style={{
        padding: '14px 20px',
        borderBottom: '1px solid var(--border)',
        background: 'var(--surface)',
        flexShrink: 0,
        display: 'flex', flexDirection: 'column', gap: 10,
      }}>
        {/* Page title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 3, height: 18, background: 'var(--orange)', borderRadius: 2, flexShrink: 0 }} />
          <span style={{ fontSize: 17, fontWeight: 700, color: 'var(--navy)' }}>Outreach Queue</span>
          <span style={{ fontSize: 13, color: 'var(--text-3)', fontWeight: 400 }}>
            {activeTotal} in queue
          </span>
        </div>

        {/* Tab switcher: 1:1 queue vs cold-blast cadence tracking */}
        <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border)' }}>
          {[{ id: 'queue', label: 'Queue' }, { id: 'cadence', label: 'Cadence' }].map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              style={{
                padding: '8px 14px', fontSize: 13, fontWeight: 600, border: 'none', background: 'none',
                cursor: 'pointer',
                color: activeTab === t.id ? 'var(--orange)' : 'var(--text-3)',
                borderBottom: activeTab === t.id ? '2px solid var(--orange)' : '2px solid transparent',
                marginBottom: -1,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {activeTab === 'queue' && <>
        {/* ICP banner */}
        {icpLoaded && <IcpBanner icp={icp} getToken={getToken} onSaved={setIcp} />}

        {/* AI deal follow-up queue */}
        <FollowUpQueue getToken={getToken} onOpenTimeline={onOpenTimeline} />

        {/* AI filter */}
        <AiFilterBar onFilter={handleAiFilter} loading={aiLoading} />
        {aiResult && (
          <div style={{ fontSize: 12, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: 'var(--orange)' }}>✦</span>
            {aiResult.explanation} — {aiResult.contact_ids.length} contact{aiResult.contact_ids.length !== 1 ? 's' : ''} shown
          </div>
        )}

        {/* Filter chips + sort */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {[
            { id: 'all', label: `All (${activeTotal})` },
            ...LANE_ORDER.map(id => ({ id, label: `${LANE_META[id].label} (${groups[id].length})` })),
            { id: 'incomplete', label: `Incomplete info (${incompleteCount})` },
          ].map(chip => (
            <button
              key={chip.id}
              onClick={() => setFilterChip(chip.id)}
              className={`btn btn--sm ${filterChip === chip.id ? 'btn--primary' : 'btn--ghost'}`}
            >
              {chip.label}
            </button>
          ))}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-3)' }}>
            Sort:
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              style={{
                padding: '4px 8px', borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)', fontSize: 12,
                background: 'var(--surface)', color: 'var(--text-2)',
              }}
            >
            <option value="priority">Priority</option>
            <option value="days">Days silent</option>
            <option value="trial">Trial end</option>
            </select>
          </div>
        </div>
        </>}
      </div>

      {activeTab === 'cadence' && <CadencePanel getToken={getToken} />}

      {/* Split pane */}
      {activeTab === 'queue' && <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Left: contact list */}
        <div style={{
          width: 340, flexShrink: 0,
          borderRight: '1px solid var(--border)',
          overflowY: 'auto',
          background: 'var(--surface)',
        }}>
          {loading ? (
            <div style={{ padding: 24, color: 'var(--text-3)', fontSize: 13 }}>Loading queue…</div>
          ) : orderedVisible.length === 0 ? (
            <div style={{ padding: 24, color: 'var(--text-3)', fontSize: 13 }}>
              {aiQuery ? 'No contacts match your filter.' : 'Queue is empty — great work!'}
            </div>
          ) : aiResult ? (
            orderedVisible.map(r => (
              <ContactRow key={r.contact_id} r={r} ampEntry={amplitudeScores?.[r.email]} bdEntry={bdMap[r.email]} selected={selected === r.contact_id} onClick={() => setSelected(r.contact_id)} />
            ))
          ) : (
            <>
              {LANE_ORDER.map((id) => {
                const rows = groups[id];
                if (!rows || rows.length === 0) return null;
                const meta = LANE_META[id];
                // Dormant collapses by default; the rest open.
                const isCollapsed = collapsedGroups[id] ?? (id === 'dormant');
                return (
                  <div key={id}>
                    <GroupHeader
                      label={meta.label}
                      count={rows.length}
                      accent={meta.accent}
                      sub={meta.action}
                      collapsed={isCollapsed}
                      onToggle={() => setCollapsedGroups(s => ({ ...s, [id]: !(s[id] ?? (id === 'dormant')) }))}
                      right={id === 'icp' ? (
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => { e.stopPropagation(); handleIcpAiRank(rows); }}
                          title="Re-rank these by ICP fit using AI + your ICP definition"
                          style={{
                            fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
                            background: icpRank ? 'var(--green-bg)' : 'var(--surface)',
                            color: icpRank ? 'var(--green)' : 'var(--orange)',
                            border: '1px solid var(--border)', flexShrink: 0, cursor: 'pointer',
                          }}
                        >
                          {icpRanking ? '…' : icpRank ? '✓ AI ranked' : '✨ AI rank'}
                        </span>
                      ) : undefined}
                    />
                    {!isCollapsed && rows.map(r => (
                      <ContactRow key={r.contact_id} r={r} ampEntry={amplitudeScores?.[r.email]} bdEntry={bdMap[r.email]} selected={selected === r.contact_id} onClick={() => setSelected(r.contact_id)} />
                    ))}
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* Right pane: when a contact is selected the detail fills from the top
            (aligns with the selected row on the left); otherwise show analytics. */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--surface)', overflow: 'hidden' }}>
          {selectedRow ? (
            <div style={{ flex: 1, overflowY: 'auto' }}>
              <DetailPanel
                r={selectedRow}
                ampEntry={amplitudeScores?.[selectedRow.email]}
                bdEntry={bdMap[selectedRow.email]}
                trials={trials}
                getToken={getToken}
                onLogged={handleLogged}
                onExcluded={handleExcluded}
                onSnooze={handleSnooze}
                onIgnored={handleIgnored}
                onOpenTimeline={onOpenTimeline}
                onContactUpdate={handleContactUpdate}
              />
            </div>
          ) : (
            <div style={{ flex: 1, overflowY: 'auto' }}>
              <RecentMeetingsPanel getToken={getToken} />
              <AnalyticsCollapsible getToken={getToken} onOpenTimeline={onOpenTimeline} />
            </div>
          )}
        </div>
      </div>}
    </div>
  );
}
